import type { AutomationId } from "@repo/app-types/entities/automation";
import { renderTeammateDisplayName } from "@repo/app-types/entities/teammate";
import { AutomationArtifactsTable } from "@repo/ui/automations/automationTables";
import { Switch } from "@repo/ui/Switch/switch";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useAutomationArtifactsQuery } from "../../api/entities/automationArtifact.js";
import { useAllTeammatesQuery } from "../../api/entities/teammate.js";
import { useAutomationDetailOutletContext } from "./automationDetailPage.js";

export const AutomationResultsTab: FC = () => {
  const { automation } = useAutomationDetailOutletContext();
  const navigate = useNavigate();

  return (
    <div className="flex w-full flex-col gap-4">
      <ArtifactsView
        automationId={automation.id}
        onArtifactAction={(artifactId) => {
          navigate(`../results/${encodeURIComponent(artifactId)}`);
        }}
      />
    </div>
  );
};

const ArtifactsView: FC<{
  automationId: AutomationId;
  onArtifactAction: (artifactId: string) => void;
}> = ({ automationId, onArtifactAction }) => {
  // Refetch whenever the results view is (re)mounted — e.g. navigating away to
  // watch a run and back — so a report produced meanwhile shows up without a
  // manual reload. Invalidation from the live run page (see
  // `automationRunDetailPage`) covers the in-flight case; this covers the rest.
  const { data: artifacts, isLoading } = useAutomationArtifactsQuery(
    automationId,
    { refetchOnMountOrArgChange: true },
  );
  const { data: teammates } = useAllTeammatesQuery();

  // Empty reports (no findings, so no alert was sent) are hidden by default;
  // the switch — shown only when there are any — reveals them.
  const [showEmptyReports, setShowEmptyReports] = useState(false);
  const emptyReportCount = useMemo(
    () => (artifacts ?? []).filter((artifact) => artifact.is_empty).length,
    [artifacts],
  );
  const visibleArtifacts = useMemo(
    () =>
      showEmptyReports
        ? artifacts
        : artifacts?.filter((artifact) => !artifact.is_empty),
    [artifacts, showEmptyReports],
  );

  const teammateNamesById = useMemo(
    () =>
      new Map(
        (teammates ?? []).map((teammate) => [
          teammate.id,
          renderTeammateDisplayName(teammate),
        ]),
      ),
    [teammates],
  );

  return (
    <div className="flex w-full flex-col gap-3">
      {emptyReportCount > 0 && (
        <div className="flex justify-end">
          <Switch
            isSelected={showEmptyReports}
            onChange={setShowEmptyReports}
            label={`Show results with no findings (${emptyReportCount})`}
          />
        </div>
      )}
      <AutomationArtifactsTable
        artifacts={visibleArtifacts}
        teammateNamesById={teammateNamesById}
        isLoading={isLoading}
        onArtifactAction={(artifact) => {
          onArtifactAction(artifact.id);
        }}
      />
    </div>
  );
};
